import {
  Flex,
  Icon,
  Checkbox,
  Text,
  TableRow,
  TableCell,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { SuccessIcon, WarningCircleIcon } from '@tonic-one/react-icons';
import { ApiTokenData, ApiData, SelectedRow } from './types';
import { useTranslation } from 'react-i18next';
import { useUtcOffsetStore } from '../../../models/useUtcOffsetModel';
import { getUtcTime } from '../../../utils/time';
import ApiTokenTableType from './ApiTokenTableType';

export default function ApiTokenTable({
  account,
  orgName,
  selectOrgId,
  selectedRow,
  setSelectedRow,
  handleClickEdit,
}: {
  account: ApiTokenData;
  orgName: string;
  selectOrgId: string;
  selectedRow: SelectedRow | null;
  setSelectedRow: (v: SelectedRow | null) => void;
  handleClickEdit: (v: ApiData) => void;
}) {
  const { orgId, apis } = account;

  return (
    <>
      {selectOrgId === 'all' && <OrgNameRow orgId={orgId} orgName={orgName} />}
      {apis.map((apiData, index) => {
        return (
          <ApiRow
            key={`${apiData.accessToken}_${index}`}
            orgId={orgId}
            apiData={apiData}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            handleClickEdit={handleClickEdit}
          />
        );
      })}
    </>
  );
}

const OrgNameRow = ({ orgId, orgName }: { orgId: string; orgName: string }) => {
  return (
    <TableRow key={orgId} width="100%">
      <TableCell width="100%">{orgName}</TableCell>
      <TableCell width="0" />
      <TableCell width="0" />
      <TableCell width="0" />
      <TableCell width="0" />
    </TableRow>
  );
};

const ApiRow = ({
  orgId,
  apiData,
  selectedRow,
  setSelectedRow,
  handleClickEdit,
}: {
  orgId: string;
  apiData: ApiData;
  selectedRow: SelectedRow | null;
  setSelectedRow: (v: SelectedRow | null) => void;
  handleClickEdit: (v: ApiData) => void;
}) => {
  const { t } = useTranslation();
  const { offset } = useUtcOffsetStore();
  const checked = selectedRow
    ? selectedRow.orgId === orgId &&
      selectedRow.accessToken === apiData.accessToken
    : false;
  const apiScope = JSON.parse(JSON.stringify(apiData.scope));
  const apiScopeName = [];
  for (const item in apiScope) {
    if (apiScope[item]) {
      apiScopeName.push(item);
    }
  }
  const ValidIcon = ({ valid }: { valid: any }) => {
    const [colorMode] = useColorMode();
    const [colorStyle] = useColorStyle({ colorMode });
    const now = Date.now();
    const expiredDate = Date.parse(valid);
    if (expiredDate > now) {
      return <Icon as={SuccessIcon} color={colorStyle.color.success} />;
    } else {
      return <Icon as={WarningCircleIcon} color={colorStyle.color.error} />;
    }
  };

  return (
    <TableRow width="100%">
      <TableCell width="32px">
        <Checkbox
          checked={checked}
          onChange={() => {
            setSelectedRow(
              checked ? null : { orgId, accessToken: apiData.accessToken }
            );
          }}
        />
      </TableCell>
      <TableCell width="35%" flexGrow="2">
        <ApiTokenTableType
          apiData={apiData}
          handleClickEdit={handleClickEdit}
        />
      </TableCell>
      <TableCell width="15%" flexGrow="1">
        {t(`administration.apiToken.table.category.${apiData.category}`)}
      </TableCell>
      <TableCell width="20%" flexGrow="1">
        {apiScopeName.map((item) => {
          return (
            <Text>{t(`administration.apiToken.table.scopeName.${item}`)}</Text>
          );
        })}
      </TableCell>
      <TableCell width="150px" flexGrow="1">
        {!apiData.expiredAt || apiData.expiredAt === 'N/A'
          ? 'N/A'
          : getUtcTime(apiData.expiredAt, offset, t('common.momentDateFormat'))}
      </TableCell>
      <TableCell width="50px" flexGrow="1">
        <Flex gap="2x" align="center">
          <ValidIcon valid={apiData.expiredAt} />
        </Flex>
      </TableCell>
    </TableRow>
  );
};
